import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { TECHNOLOGIES } from '../data/companyData';
import { Layout, PenTool, Image, FileText, Code, BookOpen, File, Cpu, Scan, Eye } from 'lucide-react';

const CATEGORIES = [
  { id: 'all', label: 'All Technologies' },
  { id: 'adobe', label: 'Adobe Suite' },
  { id: 'publishing', label: 'XML & EPUB' },
  { id: 'editorial', label: 'Editorial & Word' },
  { id: 'accessibility', label: 'Accessibility Tools' },
  { id: 'ai', label: 'AI Automation' }
];

export const TechnologiesSection: React.FC = () => {
  const [selectedCat, setSelectedCat] = useState<string>('all');

  const getTechIcon = (iconName: string) => {
    switch (iconName) {
      case 'Layout': return Layout;
      case 'PenTool': return PenTool;
      case 'Image': return Image;
      case 'FileText': return FileText;
      case 'Code': return Code;
      case 'BookOpen': return BookOpen;
      case 'File': return File;
      case 'Cpu': return Cpu;
      case 'Scan': return Scan;
      default: return Eye;
    }
  };

  const filteredTech = selectedCat === 'all'
    ? TECHNOLOGIES
    : TECHNOLOGIES.filter((t) => t.category === selectedCat);

  return (
    <section id="technologies" className="py-16 sm:py-24 bg-slate-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-3xl mx-auto space-y-4"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-purple-100 border border-purple-200 text-purple-800 text-xs font-bold uppercase tracking-wider">
            <Cpu className="w-3.5 h-3.5 text-purple-600" /> Technology Ecosystem
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Industry-Standard Tools & Intelligent AI Stacks
          </h2>
          <p className="text-slate-600 text-base sm:text-lg">
            We master enterprise desktop publishing suites and automated conversion engines to process complex multi-media manuscripts.
          </p>
        </motion.div>

        {/* Category Filter Buttons */}
        <div 
          className="mt-8 flex flex-wrap justify-center gap-2 max-w-4xl mx-auto" 
          role="tablist" 
          aria-label="Technology categories"
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              role="tab"
              id={`tech-tab-${cat.id}`}
              aria-controls={`tech-tabpanel-${cat.id}`}
              onClick={() => setSelectedCat(cat.id)}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                selectedCat === cat.id
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20 scale-105 ring-2 ring-purple-300'
                  : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
              aria-selected={selectedCat === cat.id}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Tech Stack Grid with Motion Animations */}
        <div 
          className="mt-10"
          role="tabpanel"
          id={`tech-tabpanel-${selectedCat}`}
          aria-labelledby={`tech-tab-${selectedCat}`}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCat}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4"
            >
              {filteredTech.map((tech, idx) => {
                const IconComp = getTechIcon(tech.icon);
                return (
                  <motion.div
                    key={tech.id}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: idx * 0.04, ease: 'easeOut' }}
                    className="bg-white rounded-2xl p-5 border border-slate-200/90 hover:border-purple-300 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center group"
                  >
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110 duration-300"
                      style={{ backgroundColor: `${tech.color}15`, color: tech.color }}
                    >
                      <IconComp className="w-7 h-7" />
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 group-hover:text-purple-700 transition-colors">
                      {tech.name}
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">
                      {tech.description}
                    </p>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </section>
  );
};

